import React from 'react';
import type { DOMConversionMap, DOMConversionOutput, EditorConfig, LexicalNode, NodeKey, SerializedLexicalNode, Spread, DOMExportOutput } from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import katex from 'katex';
import EquationComponent from './EquationComponent';
const LEXICAL_EQUATION = 'data-lexical-equation';
const LEXICAL_INLINE = 'data-lexical-inline';
export type SerializedEquationNode = Spread<{
  equation: string;
  inline: boolean;
}, SerializedLexicalNode>;
const $convertEquationElement = (domNode: HTMLElement): null | DOMConversionOutput => {
  // this attributes are used to store to equation related information in the DOM which will helpful while performing the conversion from DOM to LexicalNode
  let equation = domNode.getAttribute(LEXICAL_EQUATION);
  const inline = domNode.getAttribute(LEXICAL_INLINE) === 'true';
  // Decode the equation from base64
  equation = atob(equation || '');
  if (equation) {
    const node = $createEquationNode(equation, inline);
    return {
      node
    };
  }
  return null;
};
export class EquationNode extends DecoratorNode<JSX.Element> {
  __equation: string;
  __inline: boolean;
  static getType(): string {
    return 'equation';
  }
  static clone(node: EquationNode): EquationNode {
    return new EquationNode(node.__equation, node.__inline, node.__key);
  }
  constructor(equation: string, inline?: boolean, key?: NodeKey) {
    super(key);
    this.__equation = equation;
    this.__inline = inline ?? false;
  }
  static importJSON(serializedNode: SerializedEquationNode): EquationNode {
    const node = $createEquationNode(serializedNode.equation, serializedNode.inline);
    return node;
  }
  exportJSON(): SerializedEquationNode {
    return {
      equation: this.getEquation(),
      inline: this.__inline,
      type: 'equation',
      version: 1
    };
  }
  createDOM(_config: EditorConfig): HTMLElement {
    const element = document.createElement(this.__inline ? 'span' : 'div');
    element.className = 'editor-equation';
    // if there are more styles addition in the future, we will use ReactDOMServer.renderToStaticMarkup to get the html element from the styled jsx component
    element.style.cursor = 'default';
    // EquationNodes should implement `user-action:none` in their CSS to avoid issues with deletion on Android.
    element.style.userSelect = 'none';
    return element;
  }
  exportDOM(): DOMExportOutput {
    const element = document.createElement(this.__inline ? 'span' : 'div');
    // Encode the equation as base64 to avoid issues with special characters
    const equation = btoa(this.__equation);
    element.setAttribute(LEXICAL_EQUATION, equation);
    element.setAttribute(LEXICAL_INLINE, `${this.__inline}`);
    katex.render(this.__equation, element, {
      displayMode: !this.__inline,
      // true === block display //
      errorColor: '#cc0000',
      output: 'html',
      strict: 'warn',
      throwOnError: false,
      trust: false
    });
    return {
      element
    };
  }
  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute(LEXICAL_EQUATION)) {
          return null;
        }
        return {
          conversion: $convertEquationElement,
          priority: 2
        };
      },
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute(LEXICAL_EQUATION)) {
          return null;
        }
        return {
          conversion: $convertEquationElement,
          priority: 1
        };
      }
    };
  }
  updateDOM(prevNode: EquationNode): boolean {
    // If the inline property changes, replace the element
    return this.__inline !== prevNode.__inline;
  }
  getTextContent(): string {
    return this.__equation;
  }
  getEquation(): string {
    return this.__equation;
  }
  setEquation(equation: string, inline: boolean): void {
    const writeable = this.getWritable();
    writeable.__equation = equation;
    writeable.__inline = inline;
  }
  decorate(): JSX.Element {
    return <EquationComponent equation={this.__equation} inline={this.__inline} nodeKey={this.__key} />;
  }
}
export const $createEquationNode = (equation = '', inline = false): EquationNode => {
  const equationNode = new EquationNode(equation, inline);
  return $applyNodeReplacement(equationNode);
};
export const $isEquationNode = (node: LexicalNode | null | undefined): node is EquationNode => {
  return node instanceof EquationNode;
};
export const $isInlineEquationNode = (node: LexicalNode | null | undefined): node is EquationNode => {
  return $isEquationNode(node) && node.__inline;
};
export const $isBlockEquationNode = (node: LexicalNode | null | undefined): node is EquationNode => {
  return $isEquationNode(node) && !node.__inline;
};